import { isImageGone } from '@components/Customization/reportImageStatus';
import { Box } from '@mui/material';
import {
  type ReportBlock, type ReportFieldItem, type ReportFieldSource, type ReportImageBlock,
} from '@store/api/reportDesignEndpoints';
import { ExclamationTriangleIcon, FileIcon, ImageIcon } from 'react-line-awesome';
import { definitionOf, fieldSourceLabels, type CatalogIcon } from '../blockCatalog';
import { useDesignPicture } from '../useDesignPicture';
import { capitals, EMPTY_VALUE, pt, toneOf, type Print, type ResolvedText, type Tone } from './print';
import { blockStyles, FLEX_ALIGN, hollowBlockStyles, PHOTO_CELLS } from './print.styles';

interface Props {
  block: ReportBlock;
  print: Print;
  /** On a navy page. */
  dark: boolean;
}

/** A block the report leaves out as it stands, or cannot draw: named, with why, so that it can be handled. */
const Hollow: React.FC<{ icon: CatalogIcon; name: string; note: string; tone: Tone; warns?: boolean }> = ({
  icon: Icon, name, note, tone, warns,
}) => (
  <Box sx={hollowBlockStyles} style={{ borderColor: tone.hairline, color: tone.muted }}>
    {warns ? <ExclamationTriangleIcon /> : <Icon />}
    <Box sx={{ minWidth: 0 }}>
      <Box component="span" sx={{ fontWeight: 600 }}>{name}</Box>
      {` · ${note}`}
    </Box>
  </Box>
);

/** What each source of a `fields` block prints for the sample (`fieldOf` in `blocks.tsx`). */
const sourceValues = ({ sample, summary, generatedAt, formatDate }: Print): Record<ReportFieldSource, string> => ({
  projectName: sample.projectName,
  inspectionName: sample.inspectionName,
  inspectionDate: formatDate(sample.created),
  modifiedDate: formatDate(sample.modified),
  generatedDate: formatDate(generatedAt),
  completion: `${summary.completionPercent}%`,
  checkpointCount: String(summary.countedPoints),
  operator: sample.operator,
  // The sample inspection carries no metadata, so an entry prints as one without a value.
  metadata: '',
});

/** The name the report prints over a value where its author gave none. Null for a source this version does not know. */
const sourceLabel = (item: ReportFieldItem, print: Print): ResolvedText | null => {
  // Only the author can name a metadata entry. Without a label its id stands in, and an id is in no language.
  if (item.source === 'metadata') return { text: item.metadataKey ?? EMPTY_VALUE, locale: 'und' };

  return item.source in fieldSourceLabels ? print.translate(fieldSourceLabels[item.source]) : null;
};

/** What an `image` block draws: an upload of the design, the project's thumbnail or the logo. */
const ImageBlockView: React.FC<{ block: ReportImageBlock; print: Print; tone: Tone; dark: boolean }> = ({
  block, print, tone, dark,
}) => {
  const { t } = print;
  const props = block.props ?? {};
  const source = props.source ?? 'upload';
  const imageId = source === 'upload' ? props.image?.imageId : undefined;
  const upload = useDesignPicture(print.images, imageId);
  const definition = definitionOf('image');
  const name = definition ? t(definition.label) : block.type;
  const hollow = (note: string, warns = false) => <Hollow icon={ImageIcon} name={name} note={note} tone={tone} warns={warns} />;

  // The sample's thumbnail stands for the project's, as it does in a preview.
  const stock = { projectThumbnail: print.sample.thumbnail, logo: print.logo };
  const address = source === 'upload' ? upload.picture : stock[source as keyof typeof stock];

  if (source === 'upload') {
    if (!imageId) return hollow(t('ReportDesign_Canvas_Empty'));
    if (upload.gone) return hollow(t('ReportDesign_Image_Missing'), true);
    // Any other failure says nothing about whether the image exists: the report may well draw it.
    if (!address && upload.error && !upload.reading && !isImageGone(upload.error)) return hollow(t('ReportDesign_Image_Unavailable'), true);
  } else if (!(source in stock)) {
    return hollow(t('ReportDesign_Block_Unknown'));
  }

  // Still being read: its room is not known yet, so nothing is drawn rather than a guess.
  if (!address) return <Box sx={blockStyles.imageBox} />;

  const picture = (
    <Box component="img" alt="" draggable={false} src={address} sx={props.frame ? [blockStyles.image, blockStyles.imageFramed] : blockStyles.image} />
  );

  return (
    <Box sx={blockStyles.imageBox} style={{ width: `${props.widthPercent ?? 100}%`, alignSelf: FLEX_ALIGN[props.align ?? 'center'] ?? 'center' }}>
      {/* On navy the white mat is its own edge. */}
      {props.frame
        ? <Box sx={dark ? blockStyles.frame : [blockStyles.frame, blockStyles.frameEdge]}>{picture}</Box>
        : picture}
    </Box>
  );
};

/**
 * One block of a cover or a closing page as the report prints it, in HTML: `renderBlock` of
 * the backend's `blocks.tsx`, case for case, with the same texts, defaults and colours and the
 * sample inspection in place of a real one.
 *
 * Where that draws nothing, because the block has nothing to print yet or is of a type this
 * version does not know, the canvas draws a labelled box instead: a block that cannot be seen
 * cannot be selected, moved or deleted either.
 */
const BlockView: React.FC<Props> = ({ block, print, dark }) => {
  const { t } = print;
  const tone = toneOf(dark);
  const definition = definitionOf(block.type);
  const name = definition ? t(definition.label) : block.type;
  const nothing = () => <Hollow icon={definition?.icon ?? FileIcon} name={name} note={t('ReportDesign_Canvas_Empty')} tone={tone} />;

  switch (block.type) {
    case 'hero': {
      const props = block.props ?? {};
      const align = props.align ?? 'left';

      return (
        <Box sx={blockStyles.hero} style={{ alignItems: FLEX_ALIGN[align], textAlign: align }}>
          <Box sx={blockStyles.heroOverline} style={{ color: tone.faint }}>
            {capitals(print.text(props.overline) ?? print.translate('Inspection_Report'))}
          </Box>
          <Box sx={blockStyles.heroTitle} style={{ color: tone.ink }}>
            {print.text(props.title)?.text ?? print.sample.inspectionName}
          </Box>
          <Box sx={blockStyles.heroSubtitle} style={{ color: tone.muted }}>
            {print.text(props.subtitle)?.text ?? print.sample.projectName}
          </Box>
          {(props.showRule ?? true) && <Box sx={blockStyles.heroRule} style={{ backgroundColor: print.accent }} />}
        </Box>
      );
    }

    case 'heading': {
      const text = print.text(block.props?.text);

      if (!text) return nothing();

      return (block.props?.level ?? 1) === 1
        ? <Box sx={blockStyles.heading1} style={{ color: tone.ink }}>{text.text}</Box>
        : <Box sx={blockStyles.heading2} style={{ color: tone.muted, borderBottomColor: tone.hairline }}>{capitals(text)}</Box>;
    }

    case 'text': {
      const props = block.props ?? {};
      const content = print.text(props.content);

      if (!content) return nothing();

      return (
        <Box
          sx={blockStyles.text}
          style={{
            fontSize: pt(props.size ?? 9),
            textAlign: props.align ?? 'left',
            color: props.style === 'muted' ? tone.muted : tone.ink,
            fontStyle: props.style === 'italic' ? 'italic' : 'normal',
          }}
        >
          {content.text}
        </Box>
      );
    }

    case 'fields': {
      const props = block.props ?? {};
      const values = sourceValues(print);
      const fields = (props.items ?? []).flatMap((item) => {
        const label = sourceLabel(item, print);

        return label ? [{ label: print.text(item.label) ?? label, value: values[item.source] }] : [];
      });

      if (!fields.length) return nothing();

      return (
        <Box sx={blockStyles.fields}>
          {fields.map((field, index) => (
            <Box key={index} sx={blockStyles.field} style={{ width: `${100 / (props.columns ?? 2)}%` }}>
              <Box sx={blockStyles.fieldLabel} style={{ color: tone.faint }}>{capitals(field.label)}</Box>
              <Box sx={blockStyles.fieldValue} style={{ color: tone.ink }}>{field.value || EMPTY_VALUE}</Box>
            </Box>
          ))}
        </Box>
      );
    }

    case 'image':
      return <ImageBlockView block={block} print={print} tone={tone} dark={dark} />;

    case 'photoGrid': {
      const { width, height } = PHOTO_CELLS[block.props?.columns ?? 2] ?? PHOTO_CELLS[2];
      const photos = print.sample.snapshots;

      if (!photos.length) return nothing();

      return (
        <Box sx={blockStyles.photoGrid}>
          {photos.map((photo, index) => (
            <Box key={index} sx={blockStyles.photoCell} style={{ width }}>
              <Box component="img" alt="" draggable={false} src={photo.picture} sx={blockStyles.photo} style={{ height: pt(height) }} />
              <Box sx={blockStyles.photoCaption} style={{ color: tone.muted }}>
                {`${index + 1}/${photos.length} • ${photo.name} • ${print.formatDateTime(photo.taken)}`}
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    case 'signatures': {
      const props = block.props ?? {};
      const labels = props.lines
        ? props.lines.map((line) => print.text(line.label))
        : [print.translate('Report_Signature_InspectedBy'), print.translate('Report_Signature_ApprovedBy')];
      const caption = (props.showDate ?? true) ? `${t('Common_Name')} / ${t('Report_Date')}` : t('Common_Name');

      if (!labels.length) return nothing();

      return (
        <Box sx={blockStyles.signatures}>
          {labels.map((label, index) => (
            <Box key={index} sx={blockStyles.signature}>
              <Box sx={blockStyles.signatureLabel} style={{ color: tone.muted }}>{label ? capitals(label) : ''}</Box>
              <Box sx={blockStyles.signatureLine} style={{ borderTopColor: tone.ink }}>
                <Box sx={blockStyles.signatureCaption} style={{ color: tone.faint }}>{caption}</Box>
              </Box>
            </Box>
          ))}
        </Box>
      );
    }

    case 'divider':
      return <Box sx={blockStyles.divider} style={{ borderBottomColor: tone.hairline }} />;

    case 'spacer':
      return <Box style={{ height: pt(block.props?.height ?? 16) }} />;

    // A design saved by a later version can hold a block this one cannot draw.
    default:
      return <Hollow icon={FileIcon} name={name} note={t('ReportDesign_Block_Unknown')} tone={tone} />;
  }
};

export default BlockView;
